import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import RouteIcon from "@mui/icons-material/Route";
import PieChartIcon from "@mui/icons-material/PieChart";
import CancelIcon from "@mui/icons-material/Cancel";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RefreshIcon from "@mui/icons-material/Refresh";
import TradeBlotterTable from "./TradeBlotterTable";
import NewOrderDialog from "./NewOrderDialog";
import BulkEdit from "./Popup/BulkEdit";
import BulkDelete from "./Popup/BulkDelete";
import DeletePopUp from "./Popup/DeletePopUp";
import EditTradeDialog from "./Popup/EditTradeDialog";
import ManageFillsDialog from "./Popup/ManageFillsDialog";
import SummaryDialog from "./SummaryDialog";
import RouteDialog from "./Popup/RouteDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  hashAction,
  hashPosition,
  findBrokerAbb,
} from "../../component/Methods";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { toast } from "react-toastify";
import { useTradeBlotterState } from "../../hooks/useOptimizedState";
import {
  getPayloadSize,
  payloadQualifiesForS3Upload,
} from "../../component/Methods/checks";
import { uploadToS3 } from "../../utils/s3Utils";
import {
  GET_SSANDC_FILLS_REQUEST,
  ALLOCATE_SSANDC_TRADES_REQUEST,
  CANCEL_SSANDC_TRADES_REQUEST,
  getStartOfLocalDay,
  getLastOrder,
  getSsandcRouterId,
  abbreviationIncludesSsandc,
  routerDisplayIncludesSsandc,
  findRouteByRouterId,
  isRouterIdSet,
  isSsandcRoutedSelectionItem,
  isSsandcRoutedOnlySelectionItem,
  getEligibleTradeIdsFromSelection,
  getTradeIdsFromSelection,
  filterTradeRowsBySelectedDate,
  filterTradeRowsByListFilters,
  collectUniqueTradeFieldValues,
  sortTickersLettersBeforeNumbers,
  formatOrderTimestamp,
  parseDistributionKeyParts,
  resolveDistributionEntities,
  resolveFilledPercentAndState,
  resolveWorkingOrderFillMetrics,
  buildTradeTypeDisplay,
  isCommissionEligibleTrade,
  buildCommissionPayloadEntry,
} from "./tradeBlotterHelpers";
import {
  tradeBlotterFilterControlSx,
  tradeBlotterOutlinedActionSx,
} from "./tradeBlotterStyles";
import { formatTickerSymbolDisplay } from "./utility";
// Loading actions are not available in todoActions - using local state instead

const TradeBlotter = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const filterControlSx = tradeBlotterFilterControlSx(theme);

  const clearDataGridSelection = useCallback(() => {
    dataGridRef.current?.clearSelection();
  }, []);

  const startLoadingRequest = useCallback(
    (requestKey, message) => {
      dispatch(addActiveRequests(requestKey));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg(message));
    },
    [dispatch]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [selectedTicker, setSelectedTicker] = useState("");
  // Initialize selectedDate to start of today (local time) to ensure consistent filtering
  const [selectedDate, setSelectedDate] = useState(() => getStartOfLocalDay());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5000);
  const [newOrderDialogOpen, setNewOrderDialogOpen] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkArchive, setIsBulkArchive] = useState(false);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTradeForEdit, setSelectedTradeForEdit] = useState(null);
  const [manageFillsDialogOpen, setManageFillsDialogOpen] = useState(false);
  const [selectedTradeForFills, setSelectedTradeForFills] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [selectedRouterOption, setSelectedRouterOption] = useState(""); // "ssc" 

  // Redux state - Using new slice system
  const { trades, deletes, brokers, strategies, funds, custodians, routes, livePrices } =
    useTradeBlotterState();

  const { userInfo, apiInfo } = useSelector((state) => state?.users);
  const isUpdateFillsLoading = useSelector((state) =>
    (state.ui?.activeRequests || []).includes(GET_SSANDC_FILLS_REQUEST)
  );

  // View state - Non-Deleted vs Deleted trades
  const [viewType, setViewType] = useState("non-deleted"); // "non-deleted" or "deleted"

  // Ref to access DataGrid Pro methods
  const dataGridRef = useRef(null);

  // Row selection is scoped to the visible date (and active vs deleted). Clear it when those change
  // so toolbar actions do not stay enabled for trades that are not on screen (e.g. after "Today").
  useEffect(() => {
    setSelectedTrades([]);
    dataGridRef.current?.clearSelection?.();
  }, [selectedDate, viewType]);

  // State to track DataGrid Pro's internal state for visual feedback
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const handleEditTrade = useCallback((tradeData) => {
    setSelectedTradeForEdit(tradeData);
    setEditDialogOpen(true);
  }, []);

  const handleManageFills = useCallback((tradeData) => {
    setSelectedTradeForFills(tradeData);
    setManageFillsDialogOpen(true);
  }, []);

  const handleSubmitFills = useCallback(async (fillData) => {
    try {
      // Create the websocket message
      const message = {
        action: "modify_trade_fills",
        data: [fillData],
      };

      startLoadingRequest("modify_trade_fills", "Updating fills...");

      sendWebSocketMessage(JSON.stringify(message));

      return Promise.resolve();
    } catch (error) {
      console.error("Error submitting fills:", error);
      return Promise.reject(error);
    }
  }, [startLoadingRequest]);
  // Function to transform Redux trade objects to table format
  const transformTradeData = (tradeObj) => {
    if (!tradeObj) {
      return null;
    }

    const lastOrder = getLastOrder(tradeObj);
    if (!lastOrder) {
      return null;
    }

    // Get the last manual or fill data
    const lastManual =
      tradeObj.manuals && tradeObj.manuals.length > 0
        ? tradeObj.manuals[tradeObj.manuals.length - 1]
        : null;

    const hasFill = tradeObj.fill && Object.keys(tradeObj.fill).length > 0;
    const hasManual = tradeObj.manuals && tradeObj.manuals.length > 0;

    // Get distribution data
    const distributionKeys = Object.keys(lastOrder.distribution || {});
    let fundName = "N/A";
    let strategyName = "N/A";
    let custodianName = "N/A";
    let totalOrderedShares = 0;

    // Determine trade type based on distribution count
    const distributionType = distributionKeys.length > 1 ? "P" : "S"; // P = Parent Trade, S = Single Trade

    if (distributionKeys.length > 0) {
      const firstKey = distributionKeys[0];
      for (const key of distributionKeys) {
        totalOrderedShares += lastOrder.distribution[key];
      }
      const resolved = resolveDistributionEntities(firstKey, {
        funds,
        strategies,
        custodians,
      });
      fundName = resolved.fundName;
      strategyName = resolved.strategyName;
      custodianName = resolved.custodianName;
    }

    // SMA: distribution key segment "1" (see NewOrderDialog); parent trades must be SMA-only on every leg
    const isSmaTrade =
      distributionKeys.length > 0 &&
      distributionKeys.every((key) => {
        const p = key.split("#");
        return p.length >= 3 && p[2] === "1";
      });

    // Determine broker abbreviation for filled percentage logic
    const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
    const isBrokerCross = brokerAbb === "cros";

    let state = "Working";
    let filledPercent = 0;
    let pricePerShare = 0;
    let filledShares = 0;
    const quantity = totalOrderedShares;

    if (hasManual) {
      pricePerShare = lastManual.sharePrice || 0;
      filledShares = lastManual.shareCount || 0;
      ({ state, filledPercent } = resolveFilledPercentAndState(
        totalOrderedShares,
        filledShares,
        isBrokerCross
      ));
    } else if (hasFill) {
      pricePerShare = tradeObj.fill.sharePrice || 0;
      filledShares = tradeObj.fill.shareCount || 0;
      ({ state, filledPercent } = resolveFilledPercentAndState(
        totalOrderedShares,
        filledShares,
        isBrokerCross
      ));
    } else {
      ({ state, filledPercent, pricePerShare, filledShares } =
        resolveWorkingOrderFillMetrics(isBrokerCross));
    }

    // Determine side from action
    const action = hashAction(lastOrder.action);
    const position = hashPosition(lastOrder.position);

    const isOptionTrade = Number(lastOrder.ticker?.investment) === 10;
    const { tradeType, isOpeningPosition } = buildTradeTypeDisplay(
      action,
      position,
      isOptionTrade
    );
    const side = tradeType;

    // Determine order type
    const orderType = lastOrder.type === 1 ? "Market" : "Limit";
    const routing = lastOrder.routing;

    // Determine if ticker is routable based on investment type
    // Investment types 8 (MoneyMarket), 9 (MutualFund), 11 (Partnership) are not routable
    const tickerInvestment = lastOrder.ticker?.investment;
    const routable =
      tickerInvestment === 8 ||
        tickerInvestment === 9 ||
        tickerInvestment === 11
        ? "No"
        : "Yes";

    // Get commission per share
    const commissionPerShare = tradeObj.commission?.commission || 0;

    // Format commission to 4 decimal places for better visibility
    const formattedCommission = Number(commissionPerShare).toFixed(4);

    const orderRouterId = tradeObj.router?.router;
    const routeMatch = findRouteByRouterId(routes, orderRouterId);
    const routerDisplay = routeMatch?.abbreviation?.toUpperCase() || "";

    const routeStatus = (() => {
      const fp = Number(filledPercent) || 0;
      if (fp >= 100) return "Filled";
      if (fp > 0) return "Partially Filled";
      if (isRouterIdSet(orderRouterId)) return "Routed";
      return "Not Routed";
    })();

    const optionsLabel = (() => {
      const o = lastOrder?.ticker?.option;
      if (o === 0 || o === "0") return "Calls";
      if (o === 1 || o === "1") return "Puts";
      return "";
    })();

    const strikePrice = (() => {
      const v = lastOrder?.ticker?.strikePrice;
      return v === undefined || v === null ? "" : v;
    })();

    const expirationDate = (() => {
      const v = lastOrder?.ticker?.expirationDate;
      if (v === undefined || v === null || v === "") return "";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    })();

    const transformedTrade = {
      id: tradeObj.tradeId,
      serialNumber: 1, // This will be set by the table
      timestamp: formatOrderTimestamp(lastOrder?.date),
      ticker:
        (lastOrder.ticker?.symbol &&
          formatTickerSymbolDisplay(lastOrder.ticker)) ||
        "N/A",
      fund: fundName,
      strategy: strategyName,
      state: state,
      lastOrder: lastOrder,
      side: side,
      routing: routing,
      routable: routable,
      routeStatus,
      cancellationStatus: (() => {
        const raw = tradeObj?.cancel?.cancellationStatus;
        if (raw === undefined || raw === null) return null;
        const n = Number(raw);
        return Number.isNaN(n) ? null : n;
      })(),
      routerDisplay,
      options: optionsLabel,
      strikePrice,
      expirationDate,
      allocation: tradeObj?.allocation ?? null,
      investment: tickerInvestment,
      isOpeningPosition: isOpeningPosition, // Add this flag for color coding
      isSmaTrade,
      quantity: totalOrderedShares, // Always show total ordered shares (1400)
      filledShares: filledShares, // Show actual filled shares (100)
      orderType: orderType,
      pricePerShare: pricePerShare,
      commPerShare: formattedCommission,
      custodian: custodianName,
      broker: (() => {
        // Try to find broker by ID first
        let brokerName = findBrokerAbb(lastOrder.broker, brokers);
        if (brokerName) return brokerName;

        // If still not found, return the original value or "N/A"
        return "N/A";
      })(),
      filledPercent: filledPercent,
      expandable: true,
      // Additional fields for reference
      tradeId: tradeObj.tradeId,
      orderDate: lastOrder.date,
      action: action,
      position: position,
      totalOrderedShares: totalOrderedShares,
      // Add process information to determine if edit should be disabled
      hasProcess: !!lastOrder.process,
      processId: lastOrder.process || null,
      // Add user information for websocket requests
      user: lastOrder.user || "asfand@plutus21.com",
      // Add distribution type (Single Trade vs Parent Trade)
      distributionType: distributionType,
      // Add order date for daily counting
      orderDate: lastOrder.date,
      // Add child trades information for Parent Trades
      childTrades: (() => {
        if (distributionType === "P") {
          return Object.entries(lastOrder.distribution || {})
            .map(([distributionKey, shareCount]) => {
              const parsed = parseDistributionKeyParts(distributionKey);
              if (!parsed || parsed.strategyHash == null) return null;
              const resolved = resolveDistributionEntities(distributionKey, {
                funds,
                strategies,
                custodians,
              });
              return {
                account: parsed.account,
                fund: resolved.fundName,
                strategy: resolved.strategyName,
                custodian: resolved.custodianName,
                sharesOrdered: shareCount,
                sharesFilled: 0,
                filledPercent: 0,
                distributionKey,
              };
            })
            .filter(Boolean);
        }
        return [];
      })(),
      // Add fields needed for EditTradeDialog
      fundAbbreviation: (() => {
        // Find fund abbreviation from fund name
        const foundFund = funds.find((f) => f.name === fundName);
        return foundFund?.abbreviation || "";
      })(),
      brokerAbbreviation: (() => {
        // Get broker abbreviation directly from the broker ID
        let brokerName = findBrokerAbb(lastOrder.broker, brokers);
        return brokerName || "";
      })(),
      account: (() => {
        // Extract account from distribution key
        if (distributionKeys.length > 0) {
          const firstKey = distributionKeys[0];
          const parts = firstKey.split("#");
          return parts[0] || ""; // "gs-wsf"
        }
        return "";
      })(),
      // Add edit handler
      onEditClick: handleEditTrade,
      // Add manage fills handler
      onManageFillsClick: handleManageFills,
    };

    return transformedTrade;
  };

  // Transform Redux trades to table format
  const transformedData = useMemo(() => {
    // Choose data source based on view type
    const dataSource = viewType === "deleted" ? deletes : trades;

    if (!dataSource || dataSource.length === 0) {
      return [];
    }

    // Group trades by date for daily counting
    const tradesByDate = {};

    dataSource.forEach((tradeObj) => {
      const transformedTrade = transformTradeData(tradeObj);
      if (transformedTrade && transformedTrade.orderDate) {
        // Convert timestamp to date string (YYYY-MM-DD format) using local date
        const tradeDateObj = new Date(transformedTrade.orderDate);
        const year = tradeDateObj.getFullYear();
        const month = String(tradeDateObj.getMonth() + 1).padStart(2, '0');
        const day = String(tradeDateObj.getDate()).padStart(2, '0');
        const tradeDate = `${year}-${month}-${day}`;

        if (!tradesByDate[tradeDate]) {
          tradesByDate[tradeDate] = [];
        }
        tradesByDate[tradeDate].push(transformedTrade);
      }
    });

    // Assign daily serial numbers and flatten the array
    const transformed = Object.entries(tradesByDate).flatMap(
      ([date, dateTrades]) => {
        return dateTrades.map((trade, index) => ({
          ...trade,
          serialNumber: index + 1, // Daily counter starting from 1
        }));
      }
    );

    return transformed;
  }, [
    trades,
    deletes,
    viewType,
    brokers,
    strategies,
    funds,
    custodians,
    handleEditTrade,
    handleManageFills,
    routes,
  ]);

  // Get unique values for dropdowns
  const uniqueFunds = useMemo(
    () => collectUniqueTradeFieldValues(transformedData, "fund"),
    [transformedData]
  );

  const uniqueStrategies = useMemo(
    () => collectUniqueTradeFieldValues(transformedData, "strategy"),
    [transformedData]
  );

  const uniqueTickers = useMemo(() => {
    const filteredByDate = filterTradeRowsBySelectedDate(
      transformedData,
      selectedDate
    );
    const rowsForTickers = filterTradeRowsByListFilters(filteredByDate, {
      searchTerm,
      selectedEntity,
      selectedStrategy,
      selectedTicker: "",
    });
    return sortTickersLettersBeforeNumbers(
      collectUniqueTradeFieldValues(rowsForTickers, "ticker")
    );
  }, [
    transformedData,
    selectedDate,
    searchTerm,
    selectedEntity,
    selectedStrategy,
  ]);

  const filteredData = useMemo(() => {
    const byDate = filterTradeRowsBySelectedDate(transformedData, selectedDate);
    return filterTradeRowsByListFilters(byDate, {
      searchTerm,
      selectedEntity,
      selectedStrategy,
      selectedTicker,
    });
  }, [
    transformedData,
    searchTerm,
    selectedEntity,
    selectedStrategy,
    selectedTicker,
    selectedDate,
  ]);

  /** Matches `handleUpdateFills`: nothing to refresh when the grid has no SS&C rows for the current date/filters. */
  const hasSsandcFillsRefreshTargets = useMemo(
    () =>
      filteredData.some((row) =>
        routerDisplayIncludesSsandc(row.routerDisplay)
      ),
    [filteredData]
  );

  /** Matches `updateCommissionForEligibleTrades`: no eligible rows for the calendar day → keep control disabled. */
  const hasCommissionEligibleTradesForDate = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return false;
    return trades.some((item) =>
      isCommissionEligibleTrade(item, selectedDate, brokers)
    );
  }, [trades, selectedDate, brokers]);

  // Memoize formatted date strings to avoid recalculating on every render
  const formattedSelectedDate = useMemo(() => {
    return selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [selectedDate]);

  const shortFormattedDate = useMemo(() => {
    return selectedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate]);

  // Memoize date input value to avoid recalculating on every render
  const dateInputValue = useMemo(() => {
    // Use local date components to avoid timezone issues with toISOString()
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [selectedDate]);

  const allocationEligibleTradeIds = useMemo(
    () =>
      getEligibleTradeIdsFromSelection(
        selectedTrades,
        routes,
        isSsandcRoutedSelectionItem
      ),
    [selectedTrades, routes]
  );

  const cancelEligibleTradeIds = useMemo(
    () =>
      getEligibleTradeIdsFromSelection(
        selectedTrades,
        routes,
        isSsandcRoutedOnlySelectionItem
      ),
    [selectedTrades, routes]
  );

  const handleNewOrder = () => {
    setNewOrderDialogOpen(true);
  };

  const handleNewOrderSubmit = (orderData) => {
    // TODO: Implement order creation logic
    // This will be integrated with the backend later
  };

  const handleUpdateSelected = () => {
    setBulkEditOpen(true);
  };

  const handleDeleteSelected = () => {
    setIsBulkArchive(false); // Ensure delete mode
    setBulkDeleteOpen(true);
  };

  const handleArchiveSelected = () => {
    const anyUnfilled = selectedTrades.some(
      (trade) => trade.filledPercent < 100
    );
    if (anyUnfilled) {
      toast.error("Cannot archive unfilled trades.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else {
      setIsBulkArchive(true); // Set archive mode
      setBulkDeleteOpen(true);
    }
  };

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
    clearDataGridSelection();
  };

  const handleBulkDeleteClose = () => {
    setBulkDeleteOpen(false);
    setIsBulkArchive(false);
    clearDataGridSelection();
  };

  const handleRouteSelect = () => {
    if (!selectedTrades || selectedTrades.length === 0) {
      toast.error("Please select at least one trade to route");
      return;
    }
    setRouteDialogOpen(true);
  };

  const handleUpdateFills = useCallback(() => {
    const ssandcRouterId = getSsandcRouterId(routes);

    const tradeIds = filteredData
      .filter((row) => routerDisplayIncludesSsandc(row.routerDisplay))
      .map((row) => String(row.tradeId));

    if (tradeIds.length === 0) {
      toast.info("No ss&c trades are available to refresh fills.");
      return;
    }

    startLoadingRequest(GET_SSANDC_FILLS_REQUEST, "Refreshing SS&C fills...");
    sendWebSocketMessage(
      JSON.stringify({
        action: GET_SSANDC_FILLS_REQUEST,
        data: {
          router: String(ssandcRouterId),
        },
      })
    );
  }, [filteredData, routes, dispatch, startLoadingRequest]);
  const handleRouteSubmit = (routerId) => {
    if (!selectedTrades || selectedTrades.length === 0) {
      toast.error("Please select at least one trade to route");
      return;
    }
    if (!routerId) {
      toast.error("Please select a router");
      return;
    }

    // Do not re-send trades that are already routed
    const eligible = selectedTrades.filter((t) => t?.tradeObject?.router == null);
    if (eligible.length === 0) {
      toast.info("Selected trades are already routed.");
      return;
    }

    const tradeIds = getTradeIdsFromSelection(eligible);

    const selectedRoute = findRouteByRouterId(routes, routerId);
    const isSsAndC = abbreviationIncludesSsandc(
      selectedRoute?.abbreviation?.toLowerCase() || ""
    );

    let action = "request_route_order";
    if (isSsAndC) {
      action = "route_ssandc";
    }

    const requestData = {
      action,
      data: {
        trades: tradeIds,
        router: String(routerId),
      },
    };

    sendWebSocketMessage(JSON.stringify(requestData));
    startLoadingRequest(isSsAndC ? "route_ssandc" : "route_orders", isSsAndC ? "Routing SS&C trades..." : "Routing EMSX trades...");
    setRouteDialogOpen(false);
  };

  const handleAllocationSubmit = useCallback(() => {
    if (!selectedTrades || selectedTrades.length === 0) {
      toast.error("Please select at least one trade to allocate");
      return;
    }

    const eligible = selectedTrades.filter((t) =>
      isSsandcRoutedSelectionItem(t, routes)
    );
    if (eligible.length === 0) {
      toast.info(
        "No SS&C routed trades with unset allocation in selection to allocate."
      );
      return;
    }

    const tradeIds = getTradeIdsFromSelection(eligible);

    startLoadingRequest(ALLOCATE_SSANDC_TRADES_REQUEST, "Sending SS&C trade allocations...");
    sendWebSocketMessage(
      JSON.stringify({
        action: ALLOCATE_SSANDC_TRADES_REQUEST,
        data: {
          trades: tradeIds,
        },
      })
    );
  }, [selectedTrades, routes, dispatch, startLoadingRequest]);

  const handleCancelTradesSubmit = useCallback(() => {
    if (!selectedTrades || selectedTrades.length === 0) {
      toast.error("Please select at least one trade to cancel");
      return;
    }

    const eligible = selectedTrades.filter((t) =>
      isSsandcRoutedOnlySelectionItem(t, routes)
    );
    if (eligible.length === 0) {
      toast.error(
        "Selected trades are not routed via SS&C. Only SS&C routed trades can be cancelled."
      );
      return;
    }

    const tradeIds = getTradeIdsFromSelection(eligible);

    startLoadingRequest(CANCEL_SSANDC_TRADES_REQUEST, "Cancelling SS&C trades...");
    sendWebSocketMessage(
      JSON.stringify({
        action: CANCEL_SSANDC_TRADES_REQUEST,
        data: {
          trades: tradeIds,
        },
      })
    );
  }, [selectedTrades, routes, dispatch, startLoadingRequest]);

  const handleTradeSelectionChange = (newSelection) => {
    // newSelection contains the row IDs from DataGrid
    // Find the corresponding trade objects from filteredData
    const selectedTradeObjects = newSelection
      .map((rowId) => filteredData.find((trade) => trade.id === rowId))
      .filter(Boolean);

    // Find the original Redux trade objects that have the orders property
    const originalTradeObjects = selectedTradeObjects
      .map((transformedTrade) => {
        const originalTrade = trades.find(
          (trade) => trade.tradeId === transformedTrade.tradeId
        );

        return {
          tradeObject: originalTrade, // The original Redux trade object with orders
          tradeId: transformedTrade.tradeId, // The actual trade ID
          filledPercent: transformedTrade.filledPercent, // Include filledPercent
        };
      })
      .filter((item) => item.tradeObject); // Only include items that have the original trade object

    setSelectedTrades(originalTradeObjects);
  };

  // Selection stores Redux trade snapshots; after route/fill/etc. those objects go stale.
  // Re-bind each selected row to the latest trade from `trades` or `deletes` by tradeId.
  useEffect(() => {
    const source =
      viewType === "deleted"
        ? Array.isArray(deletes)
          ? deletes
          : []
        : Array.isArray(trades)
          ? trades
          : [];

    setSelectedTrades((prev) => {
      if (!prev?.length) return prev;
      let changed = false;
      const next = prev.map((item) => {
        if (item?.tradeId == null) return item;
        const fresh = source.find(
          (t) =>
            t?.tradeId != null &&
            (t.tradeId === item.tradeId || String(t.tradeId) === String(item.tradeId))
        );
        if (!fresh) return item;
        if (fresh === item.tradeObject) return item;
        changed = true;
        return { ...item, tradeObject: fresh };
      });
      return changed ? next : prev;
    });
  }, [trades, deletes, viewType]);

  const updateCommissionForEligibleTrades = async () => {
    if (!Array.isArray(trades) || trades.length === 0) return;
    const eligibleData = trades.filter((item) =>
      isCommissionEligibleTrade(item, selectedDate, brokers)
    );

    if (eligibleData.length === 0) {
      toast.error(
        "No eligible trades found with Cantor or Goldman Sachs brokers"
      );
      return;
    }
    const commissionPayload = eligibleData.map((trade) =>
      buildCommissionPayloadEntry(trade, brokers)
    );

    const webSocketPayload = {
      action: "modify_trade_commissions",
      data: commissionPayload,
    };

    if (payloadQualifiesForS3Upload(getPayloadSize(webSocketPayload))) {
      try {
        const s3Result = await uploadToS3(
          webSocketPayload,
          apiInfo,
          userInfo,
          "modify_trade_commissions"
        );

        if (s3Result.success) {
          const s3ReferencePayload = {
            action: "modify_trade_commissions",
            file: s3Result.fileName,
          };

          sendWebSocketMessage(JSON.stringify(s3ReferencePayload));
        } else {
          toast.error(
            "Failed to upload commission data to S3. Please try again later."
          );
          return;
        }
      } catch (error) {
        toast.error(
          "Failed to upload commission data to S3. Please try again later."
        );
        return;
      }
    } else {
      const payloadString = JSON.stringify(webSocketPayload);
      sendWebSocketMessage(payloadString);
    }

    startLoadingRequest("modify_trade_commissions", "Auto Filling Broker Commissions...");
  };

  return (
    <>
      <Helmet>
        <title>
          {viewType === "deleted" ? "Deleted Trades" : "Trade Blotter"} |
          Vantage Alpha
        </title>
      </Helmet>

      <Box sx={{ width: "100%", overflow: "hidden" }}>
        {/* Main Content */}
        <Box
          sx={{
            width: "100%",
            height: "calc(100vh + 150px)",
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {/* Search and Controls Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {/* Top Row - Search and Main Controls */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* Left Side - Search */}
              <TextField
                size="small"
                placeholder="Search tickers, funds, or strategies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.disabled }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 400,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.paper,
                    "& fieldset": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />

              {/* Right Side - Status and Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* View Type Indicator */}
                <Chip
                  label={`${viewType === "deleted" ? "Deleted" : "Active"
                    } Trades (${filteredData.length})`}
                  color={viewType === "deleted" ? "error" : "success"}
                  size="small"
                  variant="outlined"
                />

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    Trade View
                  </InputLabel>
                  <Select
                    value={viewType}
                    onChange={(e) => setViewType(e.target.value)}
                    label="Trade View"
                    sx={filterControlSx}
                  >
                    <MenuItem value="non-deleted">Active Trades</MenuItem>
                    <MenuItem value="deleted">Deleted Trades</MenuItem>
                  </Select>
                </FormControl>

                {/* Help text */}
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                    maxWidth: 200,
                    lineHeight: 1.2,
                  }}
                >
                  Switch between active and deleted trades for the selected date
                </Typography>
              </Box>
            </Box>

            {/* Bottom Row - Filters and Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left Side - Filters */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    FUNDS
                  </InputLabel>
                  <Select
                    value={selectedEntity}
                    onChange={(e) => setSelectedEntity(e.target.value)}
                    label="FUNDS"
                    sx={filterControlSx}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {uniqueFunds.map((fund) => (
                      <MenuItem key={fund} value={fund}>
                        {fund}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    STRATEGIES
                  </InputLabel>
                  <Select
                    value={selectedStrategy}
                    onChange={(e) => setSelectedStrategy(e.target.value)}
                    label="STRATEGIES"
                    sx={filterControlSx}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {uniqueStrategies.map((strategy) => (
                      <MenuItem key={strategy} value={strategy}>
                        {strategy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: theme.palette.text.secondary }}>
                    TICKERS
                  </InputLabel>
                  <Select
                    value={selectedTicker}
                    onChange={(e) => setSelectedTicker(e.target.value)}
                    label="TICKERS"
                    sx={filterControlSx}
                  >
                    <MenuItem value="">Select an option</MenuItem>
                    {uniqueTickers.map((ticker) => (
                      <MenuItem key={ticker} value={ticker}>
                        {ticker}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ minWidth: 100 }}>
                  <TextField
                    type="date"
                    label="DATE"
                    size="small"
                    fullWidth
                    value={dateInputValue}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      // Create date at local midnight using explicit local date components
                      const [year, month, day] = selectedValue.split('-').map(Number);
                      const localDate = new Date(year, month - 1, day);
                      setSelectedDate(localDate);
                    }}
                    sx={filterControlSx}
                  />
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedDate(getStartOfLocalDay())}
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    px: 2,
                  }}
                >
                  Today
                </Button>

                {/* Filter Icon - Triggers DataGrid's built-in filter panel */}
                <Tooltip title="Advanced Filters">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (dataGridRef.current) {
                        // Toggle the filter panel
                        dataGridRef.current.toggleFilterPanel();
                      }
                    }}
                    sx={{
                      borderRadius: 1,
                      color: theme.palette.text.primary,
                      position: "relative",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <FilterListIcon />
                    {/* Filter indicator badge */}
                    {hasActiveFilters && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: -4,
                          right: -4,
                          backgroundColor: theme.palette.error.main,
                          color: "white",
                          borderRadius: "50%",
                          width: 18,
                          height: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        !
                      </Box>
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Right Side - Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Action Buttons - Always visible but only enabled when trades are selected */}

                <Tooltip
                  title={
                    isUpdateFillsLoading
                      ? "Refreshing…"
                      : !hasSsandcFillsRefreshTargets
                        ? "No SS&C trades in the current view to refresh"
                        : "Refresh"
                  }
                  arrow
                  placement="bottom"
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleUpdateFills}
                      disabled={
                        isUpdateFillsLoading || !hasSsandcFillsRefreshTargets
                      }
                      aria-label="Refresh"
                      sx={{
                        width: 36,
                        height: 36,
                        border: `1px solid ${theme.palette.divider}`,
                        color: theme.palette.primary.main,
                        backgroundColor: theme.palette.background.paper,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.primary.light,
                        },
                      }}
                    >
                      {isUpdateFillsLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <RefreshIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => setSummaryDialogOpen(true)}
                  disabled={filteredData.length === 0}
                  sx={tradeBlotterOutlinedActionSx(theme, "#009688", "#4db6ac")}
                >
                  View Summary
                </Button>

                {/* I want this button to be of some other color */}
                <Button
                  variant="outlined"
                  // suggest some other icon instead of edit icon
                  startIcon={<AutoFixHighIcon />}
                  onClick={updateCommissionForEligibleTrades}
                  disabled={!hasCommissionEligibleTradesForDate}
                  sx={tradeBlotterOutlinedActionSx(theme, "#ff9800", "#ffb74d")}
                >
                  Auto Fill Commissions
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleUpdateSelected}
                  disabled={selectedTrades.length === 0}
                  sx={tradeBlotterOutlinedActionSx(theme, "#d8e10d", "#00ff88", {
                    hoverTextColor: "#000000",
                    hoverGradientEnd: "#00cc6a",
                  })}
                >
                  Update Selected
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  disabled={selectedTrades.length === 0}
                  sx={tradeBlotterOutlinedActionSx(theme, "#d32f2f", "#ef5350", {
                    disabledUsesMutedBorder: true,
                  })}
                >
                  Delete Selected
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<ArchiveIcon />}
                  onClick={handleArchiveSelected}
                  disabled={selectedTrades.length === 0}
                  sx={tradeBlotterOutlinedActionSx(theme, "#1976d2", "#42a5f5", {
                    disabledUsesMutedBorder: true,
                  })}
                >
                  Archive Selected
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<RouteIcon />}
                  onClick={handleRouteSelect}
                  disabled={selectedTrades.length === 0}
                  sx={tradeBlotterOutlinedActionSx(theme, "#4caf50", "#81c784")}
                >
                  Route Selected
                </Button>

                <Tooltip title="Allocate selected SS&C routed trades that have no allocation yet" arrow placement="bottom">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<PieChartIcon />}
                      onClick={handleAllocationSubmit}
                      disabled={allocationEligibleTradeIds.length === 0}
                      sx={tradeBlotterOutlinedActionSx(theme, "#7b1fa2", "#ba68c8")}
                    >
                      Send SS&C allocations
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title="Cancel selected trades that are routed via SS&C"
                  arrow
                  placement="bottom"
                >
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelTradesSubmit}
                      disabled={cancelEligibleTradeIds.length === 0}
                      sx={tradeBlotterOutlinedActionSx(theme, "#c62828", "#ef5350")}
                    >
                      Cancel Trades
                    </Button>
                  </span>
                </Tooltip>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewOrder}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    backgroundColor: theme.palette.primary.main,
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  New order
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {/* Date Summary */}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Showing {viewType === "deleted" ? "deleted" : "active"} trades
                for: <strong>{formattedSelectedDate}</strong>
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {filteredData.length} {viewType === "deleted" ? "deleted" : ""}{" "}
                trade{filteredData.length !== 1 ? "s" : ""} found
              </Typography>
            </Box>

            {transformedData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No {viewType === "deleted" ? "Deleted" : "Active"} Trade Data
                  Available
                </Typography>
                <Typography variant="body2">
                  {viewType === "deleted"
                    ? "No deleted trades found for the selected date."
                    : "Trades will appear here once they are added to the system."}
                </Typography>
              </Box>
            ) : filteredData.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="h5" sx={{ mb: 1 }}>
                  No {viewType === "deleted" ? "Deleted" : "Active"} Trades
                  Found for {shortFormattedDate}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {viewType === "deleted"
                    ? "No deleted trades found for this date. Try selecting a different date."
                    : "Try selecting a different date or check if there are trades on other dates."}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedDate(getStartOfLocalDay())}
                  sx={{ textTransform: "none" }}
                >
                  Go to Today
                </Button>
              </Box>
            ) : (
              <TradeBlotterTable
                data={filteredData}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                ref={dataGridRef}
                onFilterStateChange={setHasActiveFilters}
                onSelectionChange={handleTradeSelectionChange}
                viewType={viewType}
                routeList={routes} // Pass routeList as a prop
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* New Order Dialog */}
      <NewOrderDialog
        open={newOrderDialogOpen}
        onClose={() => setNewOrderDialogOpen(false)}
        onSubmit={handleNewOrderSubmit}
      />

      {/* Bulk Edit Dialog */}
      <BulkEdit
        bulkEdit={bulkEditOpen}
        setBulkEdit={handleBulkEditClose}
        selectionModel={selectedTrades}
      />

      {/* Bulk Delete Dialog - Handles both delete and archive operations */}
      <BulkDelete
        bulkdelete={bulkDeleteOpen}
        setBulkdelete={handleBulkDeleteClose}
        selectionModel={selectedTrades}
        isBulkArchive={isBulkArchive} // true = archive mode, false = delete mode
        setIsBulkArchive={setIsBulkArchive}
      />

      {/* Delete PopUp Dialog */}
      <DeletePopUp
        openDeletePopup={deletePopUpOpen}
        setDeleteOpenPopup={setDeletePopUpOpen}
        recordForDelete={selectedTrades[0]}
        deleteObj="delete_trade"
        item="trade"
      />

      <EditTradeDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTradeForEdit(null);
        }}
        tradeData={selectedTradeForEdit}
      />

      <ManageFillsDialog
        open={manageFillsDialogOpen}
        onClose={() => {
          setManageFillsDialogOpen(false);
          setSelectedTradeForFills(null);
        }}
        tradeData={selectedTradeForFills}
        onSubmit={handleSubmitFills}
      />

      <SummaryDialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        tradesData={filteredData}
        livePrices={livePrices}
        funds={funds}
      />

      <RouteDialog
        open={routeDialogOpen}
        onClose={() => setRouteDialogOpen(false)}
        onSubmit={handleRouteSubmit}
        selectedRouterOption={selectedRouterOption}
        setSelectedRouterOption={setSelectedRouterOption}
      />

    </>
  );
};

export default TradeBlotter;
